/**
 * FileTree Component
 * Recursive tree view for displaying artifacts organized by storage path
 */

import React from 'react';
import { Card } from '../UI/Card';
import { Badge } from '../UI/Badge';
import { 
  Folder, 
  FolderOpen, 
  FileText, 
  ChevronRight, 
  ChevronDown,
  Network,
} from 'lucide-react';
import { TreeNode } from '../../utils/pathParser';
import { RegistryMetaBadges } from './RegistryMetaBadges';
import type { Artifact } from '../../types/api';

interface FileTreeProps {
  root: TreeNode;
  expandedPaths: Set<string>;
  onTogglePath: (path: string) => void;
  onArtifactClick: (artifact: Artifact) => void;
  selectedArtifactId: string | null;
  searchTerm: string;
}

export function FileTree({
  root,
  expandedPaths,
  onTogglePath,
  onArtifactClick,
  selectedArtifactId,
  searchTerm,
}: FileTreeProps) {
  const renderNode = (node: TreeNode, depth: number = 0): React.ReactNode => {
    const isExpanded = expandedPaths.has(node.fullPath);
    const isSelected = node.artifacts.some(a => a.id === selectedArtifactId);
    const hasChildren = node.children.length > 0;
    const indent = depth * 24;

    // Check if node matches search
    const matchesSearch = searchTerm === '' || 
      node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      node.artifacts.some(a => 
        a.filename?.toLowerCase().includes(searchTerm.toLowerCase())
      );

    if (!matchesSearch && !hasChildren) {
      return null;
    }

    return (
      <div key={node.fullPath}>
        {/* Folder/File Node */}
        <div
          className={`flex items-center gap-2 py-2 px-3 rounded-lg transition-colors cursor-pointer ${
            isSelected 
              ? 'bg-blue-100 dark:bg-blue-900 border border-blue-300' 
              : 'hover:bg-gray-50 dark:hover:bg-gray-800'
          }`}
          style={{ paddingLeft: `${12 + indent}px` }}
          onClick={() => {
            if (node.type === 'folder' && hasChildren) {
              onTogglePath(node.fullPath);
            } else if (node.artifacts.length > 0) {
              onArtifactClick(node.artifacts[0]);
            }
          }}
        >
          {/* Expand/Collapse Icon */}
          <div className="w-4 flex items-center justify-center">
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              )
            ) : (
              <div className="w-4" />
            )}
          </div>

          {/* Folder/File Icon */}
          {node.type === 'folder' ? (
            isExpanded ? (
              <FolderOpen className="h-5 w-5 text-blue-600" />
            ) : (
              <Folder className="h-5 w-5 text-blue-600" />
            )
          ) : (
            <FileText className="h-5 w-5 text-gray-600" />
          )}

          {/* Name */}
          <div className="flex-1 flex items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {node.name}
            </span>
            
            {/* Badges */}
            <div className="flex items-center gap-1">
              {node.type === 'folder' && (
                <>
                  <Badge variant="default" className="text-xs">
                    {node.fileCount} {node.fileCount === 1 ? 'file' : 'files'}
                  </Badge>
                  {node.totalSize > 0 && (
                    <Badge variant="default" className="text-xs">
                      {(node.totalSize / 1024 / 1024).toFixed(1)} MB
                    </Badge>
                  )}
                </>
              )}
              {node.fullPath.startsWith('unc://') || node.fullPath.includes('\\\\') ? (
                <Badge variant="default" className="bg-blue-100 text-blue-800 text-xs">
                  <Network className="h-3 w-3 mr-1" />
                  UNC
                </Badge>
              ) : null}
            </div>
          </div>
        </div>

        {/* Children (if expanded) */}
        {isExpanded && hasChildren && (
          <div>
            {node.children.map(child => renderNode(child, depth + 1))}
          </div>
        )}

        {/* Artifacts in this node (for files or folders with direct artifacts) */}
        {node.artifacts.length > 0 && (
          <div style={{ paddingLeft: `${36 + indent}px` }}>
            {node.artifacts.map(artifact => (
              <div
                key={artifact.id}
                className={`py-1 px-3 rounded text-sm cursor-pointer transition-colors ${
                  artifact.id === selectedArtifactId
                    ? 'bg-blue-50 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onArtifactClick(artifact);
                }}
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="truncate">{artifact.filename}</span>
                    <Badge variant="outline" className="text-xs">
                      {artifact.artifact_type}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {((artifact.file_size_bytes || 0) / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                  <RegistryMetaBadges artifact={artifact} compact />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="p-4">
      <div className="space-y-1">
        {root.children.map(child => renderNode(child, 0))}
        {root.children.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Folder className="h-12 w-12 mx-auto mb-2 text-gray-400" />
            <p>No files in tree structure</p>
          </div>
        )}
      </div>
    </Card>
  );
}

